import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface ThemeColors {
	primaryColor: string;
	secondaryColor: string;
	surfaceColor?: string;
	surfaceLight?: string;
	surfaceDark?: string;
	primaryDark?: string;
	secondaryLight?: string;
	outlineMain?: string;
	outlineDark?: string;
	textMainDark2?: string;
	textMainMain2?: string;
	textSecondaryMain?: string;
	tertiaryColor?: string;
	surfaceTint?: string;
	successLight?: string;
	successDark?: string;
	brandFaint?: string;
	dividerColor?: string;
	surfaceTwo?: string;
	textPrimaryMain?: string;
	fontFamily?: string;
}

export interface ThemeStore {
	primaryColor: string | null;
	secondaryColor: string | null;
	surfaceColor: string | null;
	surfaceLight: string | null;
	surfaceDark: string | null;
	primaryDark: string | null;
	secondaryLight: string | null;
	outlineMain: string | null;
	outlineDark: string | null;
	textMainDark2: string | null;
	textMainMain2: string | null;
	textSecondaryMain: string | null;
	tertiaryColor: string | null;
	surfaceTint: string | null;
	successLight: string | null;
	successDark: string | null;
	brandFaint: string | null;
	dividerColor: string | null;
	surfaceTwo: string | null;
	textPrimaryMain: string | null;
	fontFamily: string | null;
	isLoaded: boolean;
	_hasHydrated: boolean;
	setThemeColors: (colors: ThemeColors) => void;
	clearThemeColors: () => void;
	setHasHydrated: (state: boolean) => void;
}

export const useThemeStore = create<ThemeStore>()(
	persist(
		(set) => ({
			primaryColor: null,
			secondaryColor: null,
			surfaceColor: null,
			surfaceLight: null,
			surfaceDark: null,
			primaryDark: null,
			secondaryLight: null,
			outlineMain: null,
			outlineDark: null,
			textMainDark2: null,
			textMainMain2: null,
			textSecondaryMain: null,
			tertiaryColor: null,
			surfaceTint: null,
			successLight: null,
			successDark: null,
			brandFaint: null,
			dividerColor: null,
			surfaceTwo: null,
			textPrimaryMain: null,
			fontFamily: null,
			isLoaded: false,
			_hasHydrated: false,
			setThemeColors: (colors: ThemeColors) =>
				set({
					primaryColor: colors.primaryColor,
					secondaryColor: colors.secondaryColor,
					surfaceColor: colors.surfaceColor || null,
					surfaceLight: colors.surfaceLight || null,
					surfaceDark: colors.surfaceDark || null,
					primaryDark: colors.primaryDark || null,
					secondaryLight: colors.secondaryLight || null,
					outlineMain: colors.outlineMain || null,
					outlineDark: colors.outlineDark || null,
					textMainDark2: colors.textMainDark2 || null,
					textMainMain2: colors.textMainMain2 || null,
					textSecondaryMain: colors.textSecondaryMain || null,
					tertiaryColor: colors.tertiaryColor || null,
					surfaceTint: colors.surfaceTint || null,
					successLight: colors.successLight || null,
					successDark: colors.successDark || null,
					brandFaint: colors.brandFaint || null,
					dividerColor: colors.dividerColor || null,
					surfaceTwo: colors.surfaceTwo || null,
					textPrimaryMain: colors.textPrimaryMain || null,
					fontFamily: colors.fontFamily || null,
					isLoaded: true,
				}),
			clearThemeColors: () =>
				set({
					primaryColor: null,
					secondaryColor: null,
					surfaceColor: null,
					surfaceLight: null,
					surfaceDark: null,
					primaryDark: null,
					secondaryLight: null,
					outlineMain: null,
					outlineDark: null,
					textMainDark2: null,
					textMainMain2: null,
					textSecondaryMain: null,
					tertiaryColor: null,
					surfaceTint: null,
					successLight: null,
					successDark: null,
					brandFaint: null,
					dividerColor: null,
					surfaceTwo: null,
					textPrimaryMain: null,
					fontFamily: null,
					isLoaded: false,
				}),
			setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),
		}),
		{
			name: "ThemeStore",
			onRehydrateStorage: () => (state) => {
				state?.setHasHydrated(true);
			},
			partialize: (state) => ({
				primaryColor: state.primaryColor,
				secondaryColor: state.secondaryColor,
				surfaceColor: state.surfaceColor,
				surfaceLight: state.surfaceLight,
				surfaceDark: state.surfaceDark,
				primaryDark: state.primaryDark,
				secondaryLight: state.secondaryLight,
				outlineMain: state.outlineMain,
				outlineDark: state.outlineDark,
				textMainDark2: state.textMainDark2,
				textMainMain2: state.textMainMain2,
				textSecondaryMain: state.textSecondaryMain,
				tertiaryColor: state.tertiaryColor,
				surfaceTint: state.surfaceTint,
				fontFamily: state.fontFamily,
				isLoaded: state.isLoaded,
			}),
		},
	),
);
